import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, EyeIcon, EyeOffIcon, ShieldCheck, Info } from "lucide-react";
import { Button, Spinner, Input } from "../../components";
import { AuthenticationService } from "../../authentication/AuthenticationService";
import { validatePassword, calculatePasswordStrength, isValidEmail } from "../../utils/validators";
import PasswordRequirements from "./components/PasswordRequirements";
import { detectPartnerFromHost } from "../../partner/detect";
import {
  getApiStatus,
  isAccountNotActivatedError,
  isTokenExpiredError,
  isTokenInvalidError,
} from "../../utils/apiErrorCode";
import { SUPPORT_EMAIL } from "../../utils/support";

/**
 * Reset Password Page
 * -------------------
 * With a valid token given in the URL, the user can set their password.
 * The same component handles two flows, distinguished by the `mode` prop
 * passed from the route definition:
 *   - mode="reset"    → POST /auth/distributor-user/reset-password
 *   - mode="activate" → POST /auth/distributor-user/init-password
 */

interface ResetPasswordPageProps {
  mode: "reset" | "activate";
}

export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ mode }) => {

  /********** STATE VARIABLES **********/
  const [resetToken, setResetToken] = useState<string>("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  // The backend distinguishes an aged-out link, an unusable one, and an account that was never
  // onboarded. This page used to collapse all three — and every 500 and timeout — into "expired".
  const [linkProblem, setLinkProblem] = useState<"expired" | "invalid" | null>(null);
  const [accountNotActivated, setAccountNotActivated] = useState(false);
  // Activation only: the backend answers 409 when this account already has a password.
  const [passwordAlreadySet, setPasswordAlreadySet] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  /********** CONSTANTS **********/
  const authService = new AuthenticationService();
  const navigate = useNavigate();
  const location = useLocation();
  const isActivation = mode === "activate";

  const partnerPreset = detectPartnerFromHost();
  const loginImage = partnerPreset.loginImgHref;

  /********** USE EFFECTS **********/
  useEffect(() => {
    // Token validation now happens at submit time on the new auth API
    // (POST /auth/distributor-user/reset-password verifies the reset-purpose token).
    // We just need to extract the token from the URL and store it for the request.
    const queryParams = new URLSearchParams(location.search);
    const t = queryParams.get("token");
    if (t) {
      // Keep the reset/activation token in component state only — never persist it
      // to localStorage. Persisting would clobber a logged-in user's session token
      // if they happened to open a stale reset link.
      setResetToken(t);
    } else {
      setLinkProblem("invalid");
    }
  }, [location]);

  // Calculate password strength
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);

  /********** HANDLERS **********/
  const handleBackToLogin = () => navigate("/login");

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    if (!newPassword) {
      setPasswordError("Password is required");
    } else if (!validatePassword(newPassword)) {
      setPasswordError("Password must be at least 8 characters and include uppercase, lowercase, and a number.");
    } else {
      setPasswordError(null);
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);

    if (newConfirmPassword !== password) {
      setConfirmPasswordError("Passwords do not match");
    } else {
      setConfirmPasswordError(null);
    }
  };

  const handleResetPassword = async () => {
    setSubmitError(null);
    if (!validatePassword(password)) {
      setPasswordError("Password must be at least 8 characters and include uppercase, lowercase, and a number");
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }

    if (!resetToken) {
      setLinkProblem("invalid");
      return;
    }

    setIsLoading(true);

    try {
      if (isActivation) {
        await authService.initPassword(password, resetToken);
      } else {
        await authService.resetPassword(password, resetToken);
      }
      setResetSuccess(true);
    } catch (error) {
      const status = getApiStatus(error);
      if (isTokenExpiredError(error)) {
        setLinkProblem("expired");
      } else if (isTokenInvalidError(error) || status === 401 || status === 404) {
        // An uncoded 401 (missing header, bad user_id claim) or a 404 (account deleted) is just
        // as dead as a tampered token. Retrying the form can never succeed.
        setLinkProblem("invalid");
      } else if (isAccountNotActivatedError(error)) {
        // A reset can never succeed for this account, so do not offer another reset email.
        setAccountNotActivated(true);
      } else if (isActivation && status === 409) {
        setPasswordAlreadySet(true);
      } else {
        // A 500 or a dropped connection is not a dead link. Keep the form and say so.
        console.error("Password reset failed:", error);
        setSubmitError(
          isActivation
            ? "We couldn't set your password. Please try again."
            : "We couldn't reset your password. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleResetPassword();
    }
  };

  /********** EXPIRED LINK EMAIL HANDLERS **********/
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      setEmailError("");
    }
  };

  const handleEmailBlur = () => {
    if (email.trim() && !isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
    }
  };

  const handleSubmitEmail = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email.trim()) {
      setEmailError("Email address is required");
      return;
    }
    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setIsSubmitting(true);
    try {
      await authService.requestPasswordReset(email);
      setIsEmailSent(true);
    } catch (error) {
      console.error("Failed to send password reset email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmitEmail();
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="w-2/5 relative flex items-center justify-center p-8 bg-cover bg-center"
        style={{ backgroundImage: `url(${loginImage})` }}
      >
      </div>

      {/* Right Side - 60% */}
      <div className="w-3/5 flex items-center justify-center bg-gray-50 p-8">
        <div className="w-full max-w-md space-y-8">

          {/* Reset Success */}
          {resetSuccess ? (
            <>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-green-100 rounded-full p-3">
                    <ShieldCheck size={40} className="text-green-600" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Success!
                </h2>
                <p className="text-gray-600">
                  {isActivation
                    ? "Your password has been successfully set. You can now log in with your new password."
                    : "Your password has been successfully reset. You can now log in with your new password."}
                </p>
              </div>

              <div className="flex flex-col gap-4 items-center">
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  onClick={handleBackToLogin}
                  tabIndex={0}
                  aria-label="Back to Login"
                >
                  Return to Login
                </Button>
              </div>
            </>
          ) : accountNotActivated ? (
            <>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-blue-100 rounded-full p-3">
                    <Info size={40} className="text-blue-600" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Finish setting up your account first
                </h2>
                <p className="text-gray-600">
                  This account hasn't been set up yet, so there's no password to reset. Use the
                  onboarding link we emailed you to create your password. After that you can reset
                  it here any time.
                </p>
                <p className="text-gray-600 mt-3">
                  Check your inbox for the invitation, and your spam folder if it isn't there. If
                  you can't find it, email us and we'll send a new one.
                </p>
              </div>

              <div className="flex flex-col gap-4 items-center">
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  tabIndex={0}
                  aria-label={`Email ${SUPPORT_EMAIL}`}
                  onClick={() =>
                    window.open(
                      `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                        "I can't find my onboarding email"
                      )}`,
                      "_self"
                    )
                  }
                >
                  Email {SUPPORT_EMAIL}
                </Button>
                <button
                  type="button"
                  className="text-sm font-medium text-black hover:text-slate-700 focus:outline-none focus:underline"
                  tabIndex={0}
                  aria-label="Back to Login"
                  onClick={handleBackToLogin}
                >
                  Back to Login
                </button>
              </div>
            </>
          ) : passwordAlreadySet ? (
            <>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-blue-100 rounded-full p-3">
                    <Info size={40} className="text-blue-600" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Your password is already set
                </h2>
                <p className="text-gray-600">
                  This account has already been set up, so this activation link can't be used again.
                  Log in with your password, or request a reset link if you've forgotten it.
                </p>
              </div>

              <div className="flex flex-col gap-4 items-center">
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  tabIndex={0}
                  aria-label="Request a new reset link"
                  onClick={() => navigate("/forgot-password")}
                >
                  Request a new reset link
                </Button>
                <button
                  type="button"
                  className="text-sm font-medium text-black hover:text-slate-700 focus:outline-none focus:underline"
                  tabIndex={0}
                  aria-label="Back to Login"
                  onClick={handleBackToLogin}
                >
                  Back to Login
                </button>
              </div>
            </>
          ) : linkProblem && isActivation ? (
            // An invitation is never re-issued by the user themselves — re-inviting is an operator
            // action, so a dead activation link routes to support, not to a reset email. A reset
            // email would not help anyway: the account has no password to reset yet.
            <>
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {linkProblem === "expired"
                    ? "This link has expired."
                    : "This link isn't valid."}
                </h2>
                <p className="text-gray-600">
                  {linkProblem === "expired"
                    ? "Activation links are valid for 7 days."
                    : "This link can't be used — it may have been altered in transit, or already used."}
                </p>
                <p className="text-gray-600 mt-2">
                  Email us and we'll send you a new one.
                </p>
              </div>

              <div className="flex flex-col gap-4 items-center">
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  tabIndex={0}
                  aria-label={`Email ${SUPPORT_EMAIL}`}
                  onClick={() =>
                    window.open(
                      `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                        "My activation link doesn't work"
                      )}`,
                      "_self"
                    )
                  }
                >
                  Email {SUPPORT_EMAIL}
                </Button>
                <button
                  type="button"
                  className="text-sm font-medium text-black hover:text-slate-700 focus:outline-none focus:underline"
                  tabIndex={0}
                  aria-label="Back to Login"
                  onClick={handleBackToLogin}
                >
                  Back to Login
                </button>
              </div>
            </>
          ) : linkProblem ? (
            <>
              {!isEmailSent ? (
                <>
                  <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      {linkProblem === "expired"
                        ? "This link has expired."
                        : "This link isn't valid."}
                    </h2>
                    <p className="text-gray-600">
                      {linkProblem === "expired"
                        ? "For security reasons, password reset links only work for a short time."
                        : "This link can't be used — it may have been altered in transit, or already used."}
                    </p>
                    <p className="text-gray-600 mt-2">
                      You can request a new one below.
                    </p>
                  </div>

                  <form onSubmit={handleSubmitEmail} className="flex flex-col gap-4 items-center">
                    <div className="w-full">
                      <Input
                        type="email"
                        label="Email Address"
                        placeholder="Enter your email"
                        value={email}
                        onChange={handleEmailChange}
                        onBlur={handleEmailBlur}
                        onKeyDown={handleEmailKeyDown}
                        required
                        aria-label="Email address input"
                        tabIndex={0}
                        error={emailError}
                      />
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full mt-4"
                      tabIndex={0}
                      aria-label="Send reset email"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleSubmitEmail(e as any);
                        }
                      }}
                      disabled={isSubmitting || !email.trim() || !isValidEmail(email)}
                    >
                      {isSubmitting ? "Sending" : "Send reset email"}
                      {isSubmitting && <Spinner className="ml-2" />}
                    </Button>

                    <div className="text-sm">
                      <button
                        type="button"
                        className="font-medium text-black hover:text-slate-700 focus:outline-none focus:underline"
                        tabIndex={0}
                        aria-label="Back to Login"
                        onClick={handleBackToLogin}
                      >
                        Back to Login
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Check Your Inbox
                    </h2>
                    <p className="text-gray-600 mb-4">
                      We've sent a password reset link to <strong>{email}</strong>
                    </p>
                  </div>

                  <div className="flex flex-col gap-4 items-center">
                    <p className="text-sm text-gray-500">
                      Didn't receive the email? Check your spam folder or try again.
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full"
                      onClick={() => {
                        setIsEmailSent(false);
                        setEmail("");
                        setEmailError("");
                      }}
                    >
                      Send email again
                    </Button>

                    <div className="text-sm">
                      <button
                        type="button"
                        className="font-medium text-black hover:text-slate-700 focus:outline-none focus:underline"
                        tabIndex={0}
                        aria-label="Back to Login"
                        onClick={handleBackToLogin}
                      >
                        Back to Login
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {isActivation ? "Set Your Password" : "Reset Your Password"}
                </h2>
                <p className="text-gray-600">
                  {isActivation ? "Choose a password for your account" : "Enter your new password below"}
                </p>
              </div>

              <div className="flex flex-col gap-4">
                {/* Password Field */}
                <div className="relative">
                  <label htmlFor="password" className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                    New Password
                    <Info
                      size={13}
                      className="ml-1 cursor-help"
                      onMouseEnter={() => setShowRequirements(true)}
                      onMouseLeave={() => setShowRequirements(false)}
                    />
                  </label>
                  <div className="relative">
                    <div className="relative">
                      <input
                        ref={passwordInputRef}
                        type={showPassword ? "text" : "password"}
                        id="password"
                        placeholder="Enter your new password"
                        value={password}
                        onChange={handlePasswordChange}
                        onKeyDown={handleKeyDown}
                        aria-invalid={!!passwordError}
                        tabIndex={0}
                        className={`border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black w-full pr-12 ${passwordError ? "border-red-500 focus:ring-red-500" : ""
                          }`}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-gray-700"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        tabIndex={0}
                      >
                        {showPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                      </button>
                    </div>
                    {passwordError && <small className="mt-1 text-xs text-red-500 block">{passwordError}</small>}
                  </div>

                  {/* Password Strength Indicator */}
                  {password && (
                    <div className="mt-2">
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ease-out ${passwordStrength === 0
                              ? "bg-transparent"
                              : passwordStrength < 25
                                ? "bg-red-500"
                                : passwordStrength < 50
                                  ? "bg-orange-500"
                                  : passwordStrength < 70
                                    ? "bg-yellow-500"
                                    : passwordStrength < 90
                                      ? "bg-green-500"
                                      : "bg-emerald-500"
                            }`}
                          style={{ width: `${passwordStrength}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span
                          className={`text-xs text-red-500 ${passwordStrength > 0 && passwordStrength < 25 ? "font-bold" : ""
                            }`}
                        >
                          Weak
                        </span>
                        <span
                          className={`text-xs text-orange-500 ${passwordStrength >= 25 && passwordStrength < 50 ? "font-bold" : ""
                            }`}
                        >
                          Fair
                        </span>
                        <span
                          className={`text-xs text-yellow-500 ${passwordStrength >= 50 && passwordStrength < 70 ? "font-bold" : ""
                            }`}
                        >
                          Good
                        </span>
                        <span
                          className={`text-xs text-green-500 ${passwordStrength >= 70 && passwordStrength < 90 ? "font-bold" : ""
                            }`}
                        >
                          Strong
                        </span>
                        <span className={`text-xs text-emerald-500 ${passwordStrength >= 90 ? "font-bold" : ""}`}>
                          Very Strong
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Password requirements component */}
                  <div className="relative">
                    <div
                      className={`absolute top-2 left-0 z-10 shadow-lg rounded-lg w-64 transition-opacity duration-150 ${showRequirements ? "opacity-100" : "opacity-0 pointer-events-none"
                        }`}
                    >
                      <PasswordRequirements password={password} passwordStrength={passwordStrength} />
                    </div>
                  </div>
                </div>

                {/* Confirm password */}
                <div className="relative">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        placeholder="Confirm your new password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        onKeyDown={handleKeyDown}
                        aria-invalid={!!confirmPasswordError}
                        tabIndex={0}
                        className={`border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black w-full pr-12 ${confirmPasswordError ? "border-red-500 focus:ring-red-500" : ""
                          }`}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-gray-700"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        tabIndex={0}
                      >
                        {showConfirmPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                      </button>
                    </div>
                    {confirmPasswordError && <small className="mt-1 text-xs text-red-500 block">{confirmPasswordError}</small>}
                  </div>
                </div>

                {submitError && (
                  <div
                    role="alert"
                    className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
                  >
                    {submitError}
                  </div>
                )}

                <Button
                  type="button"
                  variant="primary"
                  className="w-full mt-4"
                  onClick={handleResetPassword}
                  disabled={!!passwordError || !!confirmPasswordError || !password || !confirmPassword || isLoading}
                  tabIndex={0}
                  aria-label="Reset password button"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleResetPassword();
                    }
                  }}
                >
                  {isLoading ? (
                    <>
                      Resetting Password
                      <Spinner className="ml-2" />
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>

                <div className="text-sm">
                  <button
                    type="button"
                    className="font-medium text-black hover:text-slate-700 focus:outline-none focus:underline flex items-center justify-center w-full"
                    onClick={handleBackToLogin}
                    tabIndex={0}
                    aria-label="Back to Login"
                  >
                    <ArrowLeft size="16" className="mr-2" /> Back to Login
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
